import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react'

import type { PostInfoV2 } from '@page/post-v2/types'
import './PostPageV2Images.scss'

const baseClass = 'PostPageV2Images'

interface Props {
    postInfo?: PostInfoV2 | undefined | null
    isLoading?: boolean
}

function PostPageV2Images({ postInfo, isLoading }: Props) {
    const images = useMemo(() => postInfo?.data?.images || [], [postInfo?.data?.images])
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [isPaused, setIsPaused] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [fullscreenIndex, setFullscreenIndex] = useState(0)
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const touchStartX = useRef<number>(0)
    const touchEndX = useRef<number>(0)

    const goToSlide = useCallback(
        (index: number, isFullscreenMode = false) => {
            if (isTransitioning) return
            const totalSlides = images.length
            const normalizedIndex = ((index % totalSlides) + totalSlides) % totalSlides

            setIsTransitioning(true)

            if (isFullscreenMode) {
                setFullscreenIndex(normalizedIndex)
            } else {
                setCurrentIndex(normalizedIndex)
            }

            setTimeout(() => {
                setIsTransitioning(false)
            }, 500)
        },
        [images.length, isTransitioning]
    )

    const goToNext = useCallback(
        (isFullscreenMode = false) => {
            const current = isFullscreenMode ? fullscreenIndex : currentIndex
            goToSlide(current + 1, isFullscreenMode)
        },
        [currentIndex, fullscreenIndex, goToSlide]
    )

    const goToPrev = useCallback(
        (isFullscreenMode = false) => {
            const current = isFullscreenMode ? fullscreenIndex : currentIndex
            goToSlide(current - 1, isFullscreenMode)
        },
        [currentIndex, fullscreenIndex, goToSlide]
    )

    const openFullscreen = useCallback((index: number) => {
        setFullscreenIndex(index)
        setIsFullscreen(true)
        document.body.style.overflow = 'hidden'
    }, [])

    const closeFullscreen = useCallback(() => {
        setIsFullscreen(false)
        document.body.style.overflow = ''
    }, [])

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isFullscreen) {
                closeFullscreen()
            }
            if (!isFullscreen) {
                if (e.key === 'ArrowLeft') goToPrev(false)
                if (e.key === 'ArrowRight') goToNext(false)
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isFullscreen, closeFullscreen, goToPrev, goToNext])

    useEffect(() => {
        if (images.length <= 1 || isPaused || isFullscreen) return

        timerRef.current = setInterval(() => {
            goToNext(false)
        }, 4000)

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
            }
        }
    }, [images.length, goToNext, isPaused, isFullscreen])

    const handleMouseEnter = useCallback(() => setIsPaused(true), [])
    const handleMouseLeave = useCallback(() => setIsPaused(false), [])

    const handleTouchStart = useCallback((e: React.TouchEvent, isFullscreenMode = false) => {
        touchStartX.current = e.touches[0].clientX
    }, [])

    const handleTouchEnd = useCallback(
        (e: React.TouchEvent, isFullscreenMode = false) => {
            touchEndX.current = e.changedTouches[0].clientX
            const difference = touchStartX.current - touchEndX.current

            if (Math.abs(difference) > 50) {
                if (difference > 0) {
                    goToNext(isFullscreenMode)
                } else {
                    goToPrev(isFullscreenMode)
                }
            }
        },
        [goToNext, goToPrev]
    )

    if (isLoading || images.length === 0) {
        return null
    }

    const renderFullscreenSlider = () => (
        <div className={`${baseClass}__fullscreen`} onClick={closeFullscreen}>
            <div
                className={`${baseClass}__fullscreen--content`}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={(e) => handleTouchStart(e, true)}
                onTouchEnd={(e) => handleTouchEnd(e, true)}
            >
                <button
                    className={`${baseClass}__fullscreen--close`}
                    onClick={closeFullscreen}
                    aria-label="Close fullscreen"
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M17.0299 2.74408C17.3554 3.06951 17.3554 3.59715 17.0299 3.92259L11.0655 9.88703L17.2559 16.0774C17.5814 16.4028 17.5814 16.9305 17.2559 17.2559C16.9305 17.5814 16.4029 17.5814 16.0774 17.2559L9.88698 11.0655L3.92259 17.0299C3.59715 17.3554 3.06951 17.3554 2.74408 17.0299C2.41864 16.7045 2.41864 16.1769 2.74408 15.8514L8.70847 9.88703L2.97007 4.14858C2.64463 3.82314 2.64463 3.2955 2.97007 2.97007C3.29551 2.64463 3.82315 2.64463 4.14858 2.97007L9.88698 8.70852L15.8514 2.74408C16.1769 2.41864 16.7045 2.41864 17.0299 2.74408Z"
                            fill="white"
                        />
                    </svg>
                </button>

                <div className={`${baseClass}__fullscreen--track`}>
                    <div
                        className={`${baseClass}__fullscreen--slides`}
                        style={{
                            transform: `translateX(-${fullscreenIndex * 100}%)`,
                            transition: isTransitioning ? 'transform 0.5s ease-in-out' : 'none',
                        }}
                    >
                        {images.map((image, index) => (
                            <div key={index} className={`${baseClass}__fullscreen--slide`}>
                                {/* Blurred background */}
                                <div
                                    className={`${baseClass}__fullscreen--slide-bg`}
                                    style={{
                                        backgroundImage: `url(${image.url})`,
                                    }}
                                />
                                {/* Main image */}
                                <img src={image.url} alt={`${image.id}`} draggable={false} />
                            </div>
                        ))}
                    </div>
                </div>

                {images.length > 1 && (
                    <>
                        <button
                            className={`${baseClass}__fullscreen--nav ${baseClass}__fullscreen--nav-left`}
                            onClick={(e) => {
                                e.stopPropagation()
                                goToPrev(true)
                            }}
                            aria-label="Previous slide"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.86627 14.4697C9.15916 14.7626 9.15916 15.2374 8.86627 15.5303C8.57337 15.8232 8.0985 15.8232 7.80561 15.5303L2.80561 10.5303C2.66496 10.3897 2.58594 10.1989 2.58594 10C2.58594 9.80109 2.66496 9.61032 2.80561 9.46967L7.80561 4.46967C8.0985 4.17678 8.57338 4.17678 8.86627 4.46967C9.15916 4.76256 9.15916 5.23744 8.86627 5.53033L5.1466 9.25H16.6693C17.0835 9.25 17.4193 9.58579 17.4193 10C17.4193 10.4142 17.0835 10.75 16.6693 10.75H5.1466L8.86627 14.4697Z"
                                    fill="white"
                                />
                            </svg>
                        </button>

                        <button
                            className={`${baseClass}__fullscreen--nav ${baseClass}__fullscreen--nav-right`}
                            onClick={(e) => {
                                e.stopPropagation()
                                goToNext(true)
                            }}
                            aria-label="Next slide"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M11.1389 14.4697C10.846 14.7626 10.846 15.2374 11.1389 15.5303C11.4318 15.8232 11.9067 15.8232 12.1996 15.5303L17.1996 10.5303C17.3403 10.3897 17.4193 10.1989 17.4193 10C17.4193 9.80109 17.3403 9.61032 17.1996 9.46967L12.1996 4.46967C11.9067 4.17678 11.4318 4.17678 11.1389 4.46967C10.846 4.76256 10.846 5.23744 11.1389 5.53033L14.8586 9.25H3.33594C2.92172 9.25 2.58594 9.58579 2.58594 10C2.58594 10.4142 2.92172 10.75 3.33594 10.75H14.8586L11.1389 14.4697Z"
                                    fill="white"
                                />
                            </svg>
                        </button>

                        <div className={`${baseClass}__fullscreen--dots`}>
                            {images.map((_, index) => (
                                <button
                                    key={index}
                                    className={`${baseClass}__fullscreen--dot ${
                                        index === fullscreenIndex ? `${baseClass}__fullscreen--dot-active` : ''
                                    }`}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        goToSlide(index, true)
                                    }}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>

                        {/*<div className={`${baseClass}__fullscreen--counter`}>*/}
                        {/*    {fullscreenIndex + 1} / {images.length}*/}
                        {/*</div>*/}
                    </>
                )}
            </div>
        </div>
    )

    return (
        <div className={baseClass}>
            <div
                className={`${baseClass}__slider`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                <div
                    className={`${baseClass}__slider--track`}
                    style={{
                        transform: `translateX(-${currentIndex * 100}%)`,
                        transition: isTransitioning ? 'transform 0.5s ease-in-out' : 'none',
                    }}
                >
                    {images.map((image, index) => (
                        <div key={index} className={`${baseClass}__slider--item`} onClick={() => openFullscreen(index)}>
                            {/* Blurred background */}
                            <div
                                className={`${baseClass}__slider--item-bg`}
                                style={{
                                    backgroundImage: `url(${image.url})`,
                                }}
                            />
                            {/* Main image */}
                            <img src={image.url} alt={`${image.id}`} loading={index === 0 ? 'eager' : 'lazy'} />
                            {/*{images.length > 1 && (*/}
                            {/*    <div className={`${baseClass}__slider--counter`}>*/}
                            {/*        {currentIndex + 1} / {images.length}*/}
                            {/*    </div>*/}
                            {/*)}*/}
                            <div className={`${baseClass}__slider--overlay`}>
                                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                    <circle cx="24" cy="24" r="20" fill="rgba(0,0,0,0.6)" />
                                    <path
                                        d="M20 16L16 16L16 20M28 16L32 16L32 20M20 32L16 32L16 28M28 32L32 32L32 28"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </div>
                        </div>
                    ))}
                </div>

                {images.length > 1 && (
                    <>
                        <button
                            className={`${baseClass}__slider--nav ${baseClass}__slider--nav-left`}
                            onClick={(e) => {
                                e.stopPropagation()
                                goToPrev(false)
                            }}
                            aria-label="Previous slide"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.86627 14.4697C9.15916 14.7626 9.15916 15.2374 8.86627 15.5303C8.57337 15.8232 8.0985 15.8232 7.80561 15.5303L2.80561 10.5303C2.66496 10.3897 2.58594 10.1989 2.58594 10C2.58594 9.80109 2.66496 9.61032 2.80561 9.46967L7.80561 4.46967C8.0985 4.17678 8.57338 4.17678 8.86627 4.46967C9.15916 4.76256 9.15916 5.23744 8.86627 5.53033L5.1466 9.25H16.6693C17.0835 9.25 17.4193 9.58579 17.4193 10C17.4193 10.4142 17.0835 10.75 16.6693 10.75H5.1466L8.86627 14.4697Z"
                                    fill="white"
                                />
                            </svg>
                        </button>

                        <button
                            className={`${baseClass}__slider--nav ${baseClass}__slider--nav-right`}
                            onClick={(e) => {
                                e.stopPropagation()
                                goToNext(false)
                            }}
                            aria-label="Next slide"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M11.1389 14.4697C10.846 14.7626 10.846 15.2374 11.1389 15.5303C11.4318 15.8232 11.9067 15.8232 12.1996 15.5303L17.1996 10.5303C17.3403 10.3897 17.4193 10.1989 17.4193 10C17.4193 9.80109 17.3403 9.61032 17.1996 9.46967L12.1996 4.46967C11.9067 4.17678 11.4318 4.17678 11.1389 4.46967C10.846 4.76256 10.846 5.23744 11.1389 5.53033L14.8586 9.25H3.33594C2.92172 9.25 2.58594 9.58579 2.58594 10C2.58594 10.4142 2.92172 10.75 3.33594 10.75H14.8586L11.1389 14.4697Z"
                                    fill="white"
                                />
                            </svg>
                        </button>

                        <div className={`${baseClass}__slider--dots`}>
                            {images.map((_, index) => (
                                <button
                                    key={index}
                                    className={`${baseClass}__slider--dot ${
                                        index === currentIndex ? `${baseClass}__slider--dot-active` : ''
                                    }`}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        goToSlide(index, false)
                                    }}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            {isFullscreen && renderFullscreenSlider()}
        </div>
    )
}

export default PostPageV2Images
